"use client";

import { HomeIcon } from "@/components/ui/home";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UsersIcon } from "@/components/ui/users";
import type { FileManagerTreeNode } from "@/hooks/use-file-manager-tree";
import { cn } from "@/lib/utils";
import { type KbDragPayload } from "./kb-utils";

type TeamTabsProps = {
  teams: FileManagerTreeNode[];
  selectedTeamId: string | null;
  onSelect: (teamNodeId: string) => void;
  loading: boolean;
  drag: KbDragPayload | null;
  dropTargetId: string | null;
  onDropTargetChange: (teamNodeId: string | null) => void;
  onDropFiles: (teamNode: FileManagerTreeNode) => void;
};

export function TeamTabs({
  teams,
  selectedTeamId,
  onSelect,
  loading,
  drag,
  dropTargetId,
  onDropTargetChange,
  onDropFiles,
}: TeamTabsProps) {
  if (loading) {
    return <Skeleton className="h-9 w-72 max-w-full rounded-full" />;
  }

  if (teams.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No teams are available for this account yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <Tabs value={selectedTeamId ?? undefined} onValueChange={onSelect}>
        <div className="-mx-1 overflow-x-auto px-1 pb-1.5">
          <TabsList variant="line" aria-label="Teams">
            {teams.map((team) => {
              // The team a file already lives in is never a drop target.
              const canDrop = !!drag && drag.canMoveTeam && drag.teamId !== team.entityId;
              const isDropTarget = canDrop && dropTargetId === team.id;

              return (
                <TabsTrigger
                  key={team.id}
                  value={team.id}
                  onDragOver={(event) => {
                    if (!canDrop) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    onDropTargetChange(team.id);
                  }}
                  onDragLeave={(event) => {
                    if (!canDrop) return;
                    if (event.currentTarget.contains(event.relatedTarget as Node)) return;
                    onDropTargetChange(null);
                  }}
                  onDrop={(event) => {
                    if (!canDrop) return;
                    event.preventDefault();
                    onDropFiles(team);
                  }}
                  className={cn(
                    "flex-none",
                    canDrop && !isDropTarget && "outline-dashed outline-1 outline-primary/50",
                    isDropTarget && "bg-primary/15 ring-2 ring-primary"
                  )}
                >
                  {team.isHome ? <HomeIcon size={16} /> : <UsersIcon size={16} />}
                  {team.name}
                  <span className="text-xs font-normal text-muted-foreground tabular-nums">
                    {team.fileCount}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>
      </Tabs>

      {drag ? (
        <p aria-live="polite" className="text-xs text-muted-foreground">
          Moving <span className="font-medium text-foreground">{drag.label}</span> — drop on a
          team to move it there.
        </p>
      ) : null}
    </div>
  );
}
